import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { CategoryId, ImportedEvent } from "@myams/shared";
import { MA_CATS } from "@myams/shared";
import { useAdminEvents, useUpdateAdminEvent } from "../../lib/queries";
import { formatEventDateShort } from "../../lib/time";

type SortKey = "date" | "title" | "category" | "source";
type SortDir = "asc" | "desc";
type StatusFilter = "all" | "upcoming" | "past" | "undated";

const CATEGORY_OPTIONS: { id: "all" | CategoryId; label: string }[] = [
  { id: "all", label: "All" },
  ...(Object.entries(MA_CATS) as [CategoryId | "all", { label: string }][])
    .filter((entry): entry is [CategoryId, { label: string }] => entry[0] !== "all")
    .map(([id, c]) => ({ id, label: c.label })),
];

function classify(e: ImportedEvent): "past" | "upcoming" | "undated" {
  const ref = e.endsAt ?? e.startsAt ?? null;
  if (ref == null) return "undated";
  return ref < Math.floor(Date.now() / 1000) ? "past" : "upcoming";
}

export function AdminEvents() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);

  const [category, setCategory] = useState<"all" | CategoryId>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [showHidden, setShowHidden] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const { data: events = [], isPending } = useAdminEvents(debouncedQ);
  const updateEvent = useUpdateAdminEvent();

  const rows = useMemo(() => {
    let out = events;
    if (!showHidden) out = out.filter((e) => !e.hidden);
    if (category !== "all") out = out.filter((e) => e.category === category);
    if (statusFilter !== "all") out = out.filter((e) => classify(e) === statusFilter);

    // Sort. Date column uses starts_at; null/undated sort last regardless
    // of direction so they don't poison the top of the list.
    const cmp = (a: ImportedEvent, b: ImportedEvent): number => {
      switch (sortKey) {
        case "date": {
          const av = a.startsAt ?? null;
          const bv = b.startsAt ?? null;
          if (av == null && bv == null) return 0;
          if (av == null) return 1;
          if (bv == null) return -1;
          return av - bv;
        }
        case "title": return (a.title ?? "").localeCompare(b.title ?? "");
        case "category": return (a.category ?? "").localeCompare(b.category ?? "");
        case "source": return (a.source ?? "").localeCompare(b.source ?? "");
      }
    };
    out = [...out].sort(cmp);
    if (sortDir === "desc") out.reverse();
    return out;
  }, [events, showHidden, category, statusFilter, sortKey, sortDir]);

  const onHeader = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir(k === "date" ? "asc" : "asc"); }
  };

  const visibleCount = rows.length;
  const totalCount = events.length;
  const hiddenCount = events.filter((e) => e.hidden).length;

  return (
    <div style={{ padding: "20px 12px 32px" }}>
      {/* Toolbar */}
      <div style={{
        position: "sticky", top: 0, zIndex: 4,
        background: "var(--ma-paper)", paddingBottom: 12,
        marginBottom: 6,
      }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="search title / description / neighborhood"
            style={{
              flex: "1 1 240px", padding: "10px 12px",
              background: "var(--ma-cream)",
              border: "var(--ma-border) solid var(--ma-ink)",
              borderRadius: "var(--ma-card-radius)",
              fontFamily: "var(--ma-body)", fontSize: 16,
              color: "var(--ma-ink)", boxSizing: "border-box", minWidth: 0,
            }}
          />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as "all" | CategoryId)}
            style={selectStyle}
          >
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            style={selectStyle}
          >
            <option value="all">All status</option>
            <option value="upcoming">Upcoming</option>
            <option value="past">Past</option>
            <option value="undated">Undated</option>
          </select>
          <label style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "8px 12px",
            background: showHidden ? "var(--ma-ink)" : "transparent",
            color: showHidden ? "var(--ma-paper)" : "var(--ma-ink)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.1em",
            textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
            whiteSpace: "nowrap",
          }}>
            <input type="checkbox" checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)}
              style={{ accentColor: "var(--ma-brick)" }} />
            show hidden ({hiddenCount})
          </label>
          <button onClick={() => navigate("/admin/events/new")} style={primaryBtn}>
            + new event
          </button>
        </div>
        <div className="ma-mono" style={{
          fontSize: 10, letterSpacing: "0.08em", color: "var(--ma-ink-soft)", marginTop: 8,
        }}>
          {visibleCount} of {totalCount} {totalCount === 1 ? "event" : "events"}
          {q && ` matching "${debouncedQ}"`}
        </div>
      </div>

      {isPending && (
        <div className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink-soft)", textAlign: "center", padding: 24 }}>
          loading events…
        </div>
      )}

      {/* Grid table */}
      <div style={{
        background: "var(--ma-cream)",
        border: "var(--ma-border) solid var(--ma-ink)",
        borderRadius: "var(--ma-card-radius)",
        overflow: "auto",
      }}>
        <table style={{
          width: "100%", borderCollapse: "collapse",
          fontFamily: "var(--ma-body)", fontSize: 13,
        }}>
          <thead>
            <tr style={{
              background: "var(--ma-ink)", color: "var(--ma-paper)",
              fontFamily: "var(--ma-mono)", fontSize: 10, letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}>
              <Th style={{ width: 90 }}>status</Th>
              <Th onClick={() => onHeader("title")} active={sortKey === "title"} dir={sortDir}>title</Th>
              <Th onClick={() => onHeader("date")} active={sortKey === "date"} dir={sortDir} style={{ width: 160 }}>date</Th>
              <Th onClick={() => onHeader("category")} active={sortKey === "category"} dir={sortDir} style={{ width: 100 }}>category</Th>
              <Th onClick={() => onHeader("source")} active={sortKey === "source"} dir={sortDir} style={{ width: 100 }}>source</Th>
              <Th style={{ width: 80, textAlign: "center" }}>visible</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <Row
                key={e.id}
                event={e}
                status={classify(e)}
                onEdit={() => navigate(`/admin/events/${encodeURIComponent(e.id)}/edit`)}
                onToggleHidden={() => updateEvent.mutate({ id: e.id, hidden: !e.hidden })}
                busy={updateEvent.isPending}
              />
            ))}
            {!isPending && rows.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: 24, textAlign: "center", color: "var(--ma-ink-soft)" }}>
                  <span className="ma-mono" style={{ fontSize: 11, letterSpacing: "0.1em" }}>
                    no events match
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, onClick, active, dir, style }: {
  children: React.ReactNode; onClick?: () => void; active?: boolean; dir?: SortDir; style?: React.CSSProperties;
}) {
  const sortable = !!onClick;
  return (
    <th
      onClick={onClick}
      style={{
        textAlign: "left", padding: "10px 14px",
        borderBottom: "var(--ma-border) solid var(--ma-ink)",
        cursor: sortable ? "pointer" : "default",
        userSelect: "none", whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
      {active && sortable && (
        <span style={{ marginLeft: 6, opacity: 0.8 }}>{dir === "asc" ? "↑" : "↓"}</span>
      )}
    </th>
  );
}

function Row({
  event, status, onEdit, onToggleHidden, busy,
}: {
  event: ImportedEvent; status: "past" | "upcoming" | "undated";
  onEdit: () => void; onToggleHidden: () => void; busy: boolean;
}) {
  const muted = status === "past" || event.hidden;
  return (
    <tr
      onClick={onEdit}
      style={{
        cursor: "pointer",
        borderBottom: "1px solid var(--ma-line)",
        background: event.hidden ? "var(--ma-paper)" : "transparent",
        opacity: muted ? 0.7 : 1,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--ma-paper)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = event.hidden ? "var(--ma-paper)" : "transparent"; }}
    >
      <td style={cellStyle}>
        <span className="ma-mono" style={{
          fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
          padding: "3px 7px", borderRadius: 2,
          background: status === "past" ? "var(--ma-ink-soft)"
            : status === "undated" ? "var(--ma-mustard)"
            : "var(--ma-canal)",
          color: "var(--ma-paper)",
        }}>
          {status}
        </span>
      </td>
      <td style={{ ...cellStyle, fontWeight: 600, color: "var(--ma-ink)" }}>
        {event.title}
        {event.hidden && (
          <span className="ma-mono" style={{
            marginLeft: 8, fontSize: 9, letterSpacing: "0.12em",
            textTransform: "uppercase", color: "var(--ma-brick)",
          }}>hidden</span>
        )}
      </td>
      <td className="ma-mono" style={{ ...cellStyle, fontSize: 12, color: "var(--ma-ink-soft)" }}>
        {event.startsAt ? formatEventDateShort(event.startsAt, event.endsAt ?? null) : "—"}
      </td>
      <td className="ma-mono" style={{ ...cellStyle, fontSize: 11, letterSpacing: "0.06em", color: "var(--ma-ink-soft)" }}>
        {event.category ?? "—"}
      </td>
      <td className="ma-mono" style={{ ...cellStyle, fontSize: 11, letterSpacing: "0.06em", color: "var(--ma-ink-soft)" }}>
        {event.source}
      </td>
      <td style={{ ...cellStyle, textAlign: "center" }}>
        <button
          onClick={(e) => { e.stopPropagation(); onToggleHidden(); }}
          disabled={busy}
          aria-label={event.hidden ? "Show event" : "Hide event"}
          title={event.hidden ? "Show — consumers will see this again" : "Hide — consumers won't see this"}
          style={{
            padding: "6px 10px",
            background: event.hidden ? "var(--ma-brick)" : "transparent",
            color: event.hidden ? "var(--ma-paper)" : "var(--ma-ink)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            fontFamily: "var(--ma-mono)", fontSize: 10, letterSpacing: "0.1em",
            textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
            opacity: busy ? 0.5 : 1,
          }}
        >
          {event.hidden ? "show" : "hide"}
        </button>
      </td>
    </tr>
  );
}

const cellStyle: React.CSSProperties = {
  padding: "10px 14px",
  verticalAlign: "middle",
};
const selectStyle: React.CSSProperties = {
  padding: "10px 12px",
  background: "var(--ma-cream)",
  border: "var(--ma-border) solid var(--ma-ink)",
  borderRadius: "var(--ma-card-radius)",
  fontFamily: "var(--ma-mono)", fontSize: 12, letterSpacing: "0.06em",
  color: "var(--ma-ink)", cursor: "pointer",
};
const primaryBtn: React.CSSProperties = {
  padding: "10px 14px",
  background: "var(--ma-ink)", color: "var(--ma-paper)",
  border: "var(--ma-border) solid var(--ma-ink)",
  borderRadius: "var(--ma-card-radius)",
  fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.12em",
  textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
};
